import React, { useState, useEffect, useCallback } from "react";
import { Button, ToastContainer, Spinner, Form, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaBan } from "react-icons/fa";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/ConfirmModal";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import {
  useAccounts,
  AccountSelect,
  financeAuthPayload,
} from "../../components/finance/AccountSelect";
import "../../Styles/yellowcalfcss/Finance.css";

const PAGE_SIZE = 100;

const emptyFilters = {
  asset_type: "",
  asset_name: "",
  asset_description: "",
  asset_date: "",
  asset_amount: "",
};

const AssetRegister = () => {
  const navigate = useNavigate();
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(emptyFilters);

  const { accounts } = useAccounts();

  // Disposal / write-off modal state
  const [showDisposeModal, setShowDisposeModal] = useState(false);
  const [disposeAsset, setDisposeAsset] = useState(null);
  const [disposeForm, setDisposeForm] = useState({
    disposal_date: "",
    sale_price: "",
    disposal_expenses: "",
    cash_account_id: "",
  });
  const [isDisposing, setIsDisposing] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Main Data Fetching
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        ...financeAuthPayload(),
        limit: PAGE_SIZE,
        page: 1,
        asset_type: filters.asset_type,
        asset_name: filters.asset_name,
        asset_description: filters.asset_description,
        asset_date: filters.asset_date,
        asset_amount: filters.asset_amount ? Number(filters.asset_amount) : "",
      };

      const response = await simplePostCall(
        ApiConfig.getassets,
        JSON.stringify(payload)
      );

      if (response && response.result) {
        setAssets(response.data || []);
      } else {
        setAssets([]);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      setAssets([]);
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Debounced so typing in a filter doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(fetchInitialData, 500);
    return () => clearTimeout(timer);
  }, [fetchInitialData]);

  // Delete Logic
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const response = await simplePostCall(
        ApiConfig.Deleteeassets,
        JSON.stringify({ ...financeAuthPayload(), asset_id: deleteTarget.asset_id })
      );

      if (response && response.result) {
        toast.success("Asset deleted successfully");
        fetchInitialData();
      } else {
        toast.error(response?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast.error("An error occurred during deletion");
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const hasFilters = Object.values(filters).some((v) => v !== "");

  const resetFilters = () => setFilters(emptyFilters);

  // Open the disposal/write-off modal pre-filled with asset context
  const openDisposeModal = (asset) => {
    setDisposeAsset(asset);
    setDisposeForm({
      disposal_date: new Date().toISOString().slice(0, 10),
      sale_price: "",
      disposal_expenses: "",
      cash_account_id: "",
    });
    setShowDisposeModal(true);
  };

  const handleDisposeChange = (e) => {
    const { name, value } = e.target;
    setDisposeForm((prev) => ({ ...prev, [name]: value }));
  };

  // Submit a disposal. The backend clears the asset's cost + accumulated
  // depreciation, posts one balanced journal, computes gain/loss, and sets
  // asset_status: "disposed". Requires the cash/bank account the sale proceeds
  // land in.
  const submitDisposal = async () => {
    if (!disposeAsset) return;
    if (!disposeForm.disposal_date) {
      toast.warning("Please select a disposal date.");
      return;
    }
    if (!disposeForm.cash_account_id) {
      toast.warning("Select the cash / bank account the proceeds go to.");
      return;
    }
    setIsDisposing(true);
    try {
      const payload = {
        ...financeAuthPayload(),
        asset_id: disposeAsset.asset_id,
        disposal_date: disposeForm.disposal_date,
        sale_price: disposeForm.sale_price
          ? Number(disposeForm.sale_price)
          : 0,
        disposal_expenses: disposeForm.disposal_expenses
          ? Number(disposeForm.disposal_expenses)
          : 0,
        cash_account_id: Number(disposeForm.cash_account_id),
      };
      const res = await simplePostCall(
        ApiConfig.assetsDispose,
        JSON.stringify(payload)
      );
      if (res?.result) {
        const gl = res.data?.gain_loss;
        const glMsg =
          gl == null
            ? ""
            : gl >= 0
            ? ` Gain on disposal: ${money(gl)}.`
            : ` Loss on disposal: ${money(Math.abs(gl))}.`;
        toast.success(`Asset disposed.${glMsg}`);
        setShowDisposeModal(false);
        fetchInitialData();
      } else {
        toast.error(res?.message || "Disposal failed");
      }
    } catch (err) {
      console.error("Dispose error:", err);
      toast.error("An error occurred during disposal.");
    } finally {
      setIsDisposing(false);
    }
  };

  const statusBadgeClass = (status) => {
    const s = String(status || "active").toLowerCase();
    if (s === "disposed") return "fin-status fin-status-disposed";
    if (s === "damaged") return "fin-status fin-status-damaged";
    return "fin-status fin-status-active";
  };

  const money = (value) => `${customer_currency} ${formatNumber(value || 0)}`;

  // Summary across the assets currently in view.
  const summary = assets.reduce(
    (acc, a) => {
      const status = String(a.asset_status || "active").toLowerCase();
      acc.cost += Number(a.asset_amount) || 0;
      if (status === "disposed") acc.disposed += 1;
      else if (status === "damaged") acc.damaged += 1;
      else acc.active += 1;
      return acc;
    },
    { cost: 0, active: 0, damaged: 0, disposed: 0 }
  );

  return (
    <div className="finance-page-container">
      <ToastContainer position="top-right" />

      {/* Topbar */}
      <div className="special-header panel-white-all">
        <h4 className="page-title">Asset Register</h4>
        <div className="action-buttons-group">
          <Link
            to="/accountant/finance/asset/create"
            style={{ textDecoration: "none" }}
          >
            <Button className="btn btn-primary">+ Add Asset</Button>
          </Link>
        </div>
      </div>

      <div className="yc-list-body">
        {/* SUMMARY */}
        <div className="fin-stat-row">
          <div className="fin-stat-card fin-accent-all">
            <span className="fin-stat-label">Total Assets</span>
            <span className="fin-stat-value">{assets.length}</span>
          </div>
          <div className="fin-stat-card fin-accent-asset">
            <span className="fin-stat-label">Total Cost</span>
            <span className="fin-stat-value">{money(summary.cost)}</span>
          </div>
          <div className="fin-stat-card fin-accent-income">
            <span className="fin-stat-label">Active</span>
            <span className="fin-stat-value">{summary.active}</span>
          </div>
          <div className="fin-stat-card fin-accent-liability">
            <span className="fin-stat-label">Damaged</span>
            <span className="fin-stat-value">{summary.damaged}</span>
          </div>
          <div className="fin-stat-card fin-accent-equity">
            <span className="fin-stat-label">Disposed</span>
            <span className="fin-stat-value">{summary.disposed}</span>
          </div>
        </div>

        {/* FILTERS */}
        <div className="yc-list-filter">
          <Form.Control
            name="asset_type"
            placeholder="Type"
            value={filters.asset_type}
            onChange={handleFilterChange}
          />
          <Form.Control
            name="asset_name"
            placeholder="Asset Name"
            value={filters.asset_name}
            onChange={handleFilterChange}
          />
          <Form.Control
            name="asset_description"
            placeholder="Description"
            value={filters.asset_description}
            onChange={handleFilterChange}
          />
          <Form.Control
            type="date"
            name="asset_date"
            value={filters.asset_date}
            onChange={handleFilterChange}
            title="Purchase date"
          />
          <Form.Control
            type="number"
            name="asset_amount"
            placeholder="Amount"
            value={filters.asset_amount}
            onChange={handleFilterChange}
          />
          {hasFilters && (
            <Button variant="secondary" onClick={resetFilters}>
              Clear
            </Button>
          )}
        </div>

        {/* TABLE HEADER */}
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-9">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">Category</div>
            <div className="yc-list-cell">Name</div>
            <div className="yc-list-cell">Location</div>
            <div className="yc-list-cell">Purchase Date</div>
            <div className="yc-list-cell">Cost</div>
            <div className="yc-list-cell">Useful Life</div>
            <div className="yc-list-cell">Status</div>
            <div className="yc-list-cell">Actions</div>
          </div>
        </div>

        {/* TABLE BODY */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : assets.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">
              {hasFilters ? "No matching assets" : "No assets yet"}
            </span>
            <span className="fin-empty-text">
              {hasFilters
                ? "Nothing matches the current filters."
                : "The fixed asset register tracks what the school owns — its cost, useful life and status — and feeds the depreciation schedule."}
            </span>
            {hasFilters ? (
              <Button variant="secondary" className="mt-2" onClick={resetFilters}>
                Clear Filters
              </Button>
            ) : (
              <Link to="/accountant/finance/asset/create">
                <Button className="btn btn-primary mt-2">Add First Asset</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="yc-list-row-list">
            {assets.map((item, index) => (
              <div key={item.asset_id} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-9">
                  <div className="yc-list-cell">{index + 1}</div>
                  <div className="yc-list-cell">{item.asset_type || "—"}</div>
                  <div
                    className="yc-list-cell fw-semibold"
                    title={item.asset_description}
                  >
                    {item.asset_name}
                  </div>
                  <div className="yc-list-cell">{item.asset_location || "—"}</div>
                  <div className="yc-list-cell">
                    {item.asset_date
                      ? new Date(item.asset_date).toLocaleDateString()
                      : "—"}
                  </div>
                  <div className="yc-list-cell">{money(item.asset_amount)}</div>
                  <div className="yc-list-cell">
                    {item.useful_life_years ?? item.asset_useful_life_years
                      ? `${
                          item.useful_life_years ??
                          item.asset_useful_life_years
                        } yrs`
                      : "—"}
                  </div>
                  <div className="yc-list-cell">
                    <span className={statusBadgeClass(item.asset_status)}>
                      {(item.asset_status || "active").toUpperCase()}
                    </span>
                  </div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon"
                      title="Edit"
                      onClick={() =>
                        navigate(
                          `/accountant/finance/asset/create/${item.asset_id}`
                        )
                      }
                    >
                      <FaEdit size={16} />
                    </button>

                    {String(item.asset_status || "active").toLowerCase() !==
                      "disposed" && (
                      <button
                        className="yc-action-icon"
                        title="Dispose / Write-Off"
                        onClick={() => openDisposeModal(item)}
                      >
                        <FaBan color="#b45309" />
                      </button>
                    )}

                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Delete"
                      onClick={() => setDeleteTarget(item)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disposal / Write-Off Modal */}
      <Modal
        show={showDisposeModal}
        onHide={() => !isDisposing && setShowDisposeModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Dispose / Write-Off Asset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {disposeAsset && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div>
                <b>{disposeAsset.asset_name}</b>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {disposeAsset.asset_type} • Purchase cost{" "}
                {money(disposeAsset.asset_amount)}
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              Disposal Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="disposal_date"
              value={disposeForm.disposal_date}
              onChange={handleDisposeChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Sale Price ({customer_currency})</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              name="sale_price"
              placeholder="0 for a write-off"
              value={disposeForm.sale_price}
              onChange={handleDisposeChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Disposal Expenses ({customer_currency})</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              name="disposal_expenses"
              placeholder="0"
              value={disposeForm.disposal_expenses}
              onChange={handleDisposeChange}
            />
            <Form.Text className="text-muted">
              Gain/Loss = Sale Price − Disposal Expenses − Net Book Value.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label>
              Proceeds To (Cash / Bank) <span className="text-danger">*</span>
            </Form.Label>
            <AccountSelect
              accounts={accounts}
              name="cash_account_id"
              types={["asset"]}
              value={disposeForm.cash_account_id}
              onChange={handleDisposeChange}
              placeholder="Select cash / bank account"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDisposeModal(false)}
            disabled={isDisposing}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={submitDisposal}
            disabled={isDisposing}
          >
            {isDisposing ? <Spinner size="sm" /> : "Confirm Disposal"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRM */}
      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        title="Delete Asset"
        message={
          deleteTarget
            ? `Permanently delete "${deleteTarget.asset_name}"? To retire an asset while keeping its history, use Dispose / Write-Off instead.`
            : ""
        }
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AssetRegister;
